import { useMemo } from 'react';
import * as Yup from 'yup';

interface Tag {
  id: string;
  name: string;
  color: string;
}

export interface TagFormValues {
  tags: Record<
    string,
    { name: string; color: string; source: 'edit' | 'create' }
  >;
}

export const useTagManagerInitialValues = (
  displayTags: Tag[]
): TagFormValues => {
  return useMemo(() => {
    const tagsObject: Record<
      string,
      { name: string; color: string; source: 'edit' | 'create' }
    > = {};
    displayTags.forEach(tag => {
      tagsObject[tag.id] = {
        name: tag.name,
        color: (tag as any).backgroundColor || tag.color, // Use backgroundColor for storage, fallback to original color
        source: 'edit' // existing tags from API
      };
    });
    return { tags: tagsObject };
  }, [displayTags]);
};

export const validateTagName = (value: string, source?: 'edit' | 'create') => {
  // Don't validate empty names for new tags being created
  if (source === 'create' && (!value || value.trim() === '')) {
    return undefined;
  }

  if (!value || value.trim() === '') {
    return 'Tag name cannot be empty';
  }
  return undefined;
};

export const validateTagNameWithDuplicates = (
  value: string,
  currentTagId: string,
  allTagValues: Record<
    string,
    { name: string; color: string; source: 'edit' | 'create' }
  >,
  source?: 'edit' | 'create'
) => {
  // First check basic validation
  const basicError = validateTagName(value, source);
  if (basicError) return basicError;

  // Skip duplicate check if value is empty
  if (!value || value.trim() === '') return undefined;

  // Check for duplicates (case-insensitive)
  const trimmedValue = value.trim().toLowerCase();
  const tagIdsWithSameName = Object.keys(allTagValues).filter(tagId => {
    const otherTagName = allTagValues[tagId]?.name?.trim().toLowerCase();
    return otherTagName === trimmedValue;
  });

  // If more than one tag has this name, flag the duplicate
  if (tagIdsWithSameName.length > 1) {
    const otherIds = tagIdsWithSameName.filter(id => id !== currentTagId);

    // An existing (edit) tag is never the culprit when the duplicate is a new (create) tag
    if (
      source === 'edit' &&
      otherIds.some(id => allTagValues[id]?.source === 'create')
    ) {
      return undefined;
    }

    // Otherwise use key order — the first tag keeps its name, the rest get the error
    const firstTagId = tagIdsWithSameName[0];
    if (currentTagId !== firstTagId) {
      return 'A tag with this name already exists.';
    }
  }

  return undefined;
};

export const tagManagerValidationSchema = Yup.object().shape({
  tags: Yup.object().shape({})
});
